import { useState } from "react";
import { Link } from "react-router-dom";
import { Search, Bot, ClipboardCheck, ShieldCheck, Flag, AlertTriangle } from "lucide-react";
import { SeoHead } from "@/components/public/SeoHead";
import { JsonLd } from "@/components/public/JsonLd";

const SECTIONS = [
  { key: "review", label: "How the review works" },
  { key: "classifications", label: "Material classifications" },
  { key: "score", label: "Sustainability score" },
  { key: "warnings", label: "Brand warnings" },
  { key: "limitations", label: "Limitations" },
];

export default function MethodologyPage() {
  const [active, setActive] = useState("review");
  const origin = typeof window !== "undefined" ? window.location.origin : "https://sustainablycurated.com";
  const pageUrl = `${origin}/methodology`;
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: "Methodology — How Sustainably Curated Reviews Brands and Materials",
      description: "How Sustainably Curated reviews materials, classifies items, and scores brands. Transparent process, editorially verified, independent of brand claims.",
      url: pageUrl,
      author: { "@type": "Person", name: "Teresa Cordeiro" },
      publisher: {
        "@type": "Organization",
        name: "Sustainably Curated",
        logo: { "@type": "ImageObject", url: `${origin}/leaf.svg` },
      },
      mainEntityOfPage: { "@type": "WebPage", "@id": pageUrl },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: origin + "/" },
        { "@type": "ListItem", position: 2, name: "Methodology", item: pageUrl },
      ],
    },
  ];

  return (
    <div className="bg-ivory" data-testid="methodology-page">
      <SeoHead
        title="Methodology — Sustainably Curated"
        description="How Sustainably Curated reviews materials, classifies items, and scores brands. Transparent process, editorially verified, independent of brand claims."
        path="/methodology"
      />
      <JsonLd data={jsonLd} />

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-8">
        <h1 className="text-[1.75rem] uppercase tracking-[0.25em] text-leaf font-medium mb-3">Methodology</h1>
        <p className="text-muted_text">
          Vague sustainability claims are everywhere in fashion. <em>"Eco-friendly"</em>, <em>"conscious"</em>, <em>"green"</em> — terms that often hide blends of plastic-based fibers behind a beautiful tag. We built Sustainably Curated because finding clothing made from genuinely sustainable, organic, biodegradable, or fully approved materials shouldn't require an investigation.
        </p>
      </section>

      <div className="border-t border-line" />

      {/* Two-column layout: sidebar nav + content */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-[260px_1fr] gap-8">
          {/* Sidebar navigation */}
          <aside className="lg:sticky lg:top-24 lg:self-start" data-testid="methodology-sidenav">
            <p className="text-xs uppercase tracking-[0.2em] text-forest/70 mb-4">On this page</p>
            <ul className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible border-l border-line">
              {SECTIONS.map((s) => {
                const isActive = active === s.key;
                return (
                  <li key={s.key} className="shrink-0">
                    <button
                      type="button"
                      onClick={() => setActive(s.key)}
                      data-testid={`methodology-nav-${s.key}`}
                      aria-current={isActive ? "true" : undefined}
                      className={`w-full text-left font-serif text-base sm:text-lg leading-snug px-4 py-2 -ml-px border-l-2 transition-colors ${
                        isActive
                          ? "border-forest text-forest font-medium"
                          : "border-transparent text-ink/70 hover:text-ink hover:border-line"
                      }`}
                    >
                      {s.label}
                    </button>
                  </li>
                );
              })}
            </ul>
          </aside>

          {/* Content panel */}
          <div data-testid={`methodology-content-${active}`}>
            {active === "review" && <ReviewSection />}
            {active === "classifications" && <ClassificationsSection />}
            {active === "score" && <ScoreSection />}
            {active === "warnings" && <WarningsSection />}
            {active === "limitations" && <LimitationsSection />}
          </div>
        </div>
      </section>
    </div>
  );
}

function ReviewSection() {
  return (
    <div>
      <ol className="space-y-3" data-testid="methodology-steps">
        {[
          { n: "01", icon: Search, title: "We add a brand to our directory.", desc: "Brands are added by our editors based on interest and reader requests." },
          { n: "02", icon: Bot, title: "Our crawler discovers their product pages.", desc: "An automated crawler walks the brand's catalog and queues every product URL it finds for editorial review." },
          { n: "03", icon: ClipboardCheck, title: "Our editors manually review each product's material composition.", desc: "Real humans read the spec, cross-check claims against label evidence, and apply our classification rules below." },
          { n: "04", icon: ShieldCheck, title: "We publish only what meets the rules.", desc: <><p>Items composed entirely of <strong className="text-leaf">GREEN</strong> materials are accepted. Clothing with <strong className="text-amber">YELLOW</strong> and <strong className="text-clay">RED</strong> materials are individually reviewed, and accepted depending on the context. <strong className="text-clay">RED</strong> materials content is capped at 5% per item.</p><p className="mt-3">Even if a clothing item is mostly made from materials we classify as <strong className="text-leaf">GREEN</strong>, if any <strong className="text-amber">YELLOW</strong> or <strong className="text-clay">RED</strong> material is present, we classify the item as <strong className="text-amber">YELLOW</strong> or <strong className="text-clay">RED</strong>. If we still decide to feature the item of clothing on the website, we clearly disclose why.</p></> },
          { n: "05", icon: Flag, title: "We keep an open line for reader reports.", desc: "Even after a clothing item has been classified and published, we accept reports from readers when the material composition on a brand's published label doesn't match the actual item. We review every report — and when a mismatch is confirmed, we post a warning flag on the clothing item and on the brand's page so other readers are informed." },
        ].map((s) => (
          <li key={s.n} className="grid sm:grid-cols-[60px_36px_1fr] gap-4 items-start bg-white border border-line rounded-2xl p-5">
            <span className="font-serif text-3xl text-forest leading-none">{s.n}</span>
            <span className="w-9 h-9 rounded-full bg-sand/60 inline-flex items-center justify-center shrink-0">
              <s.icon className="w-4 h-4 text-forest" />
            </span>
            <div>
              <h3 className="font-serif text-lg leading-snug mb-1">{s.title}</h3>
              <p className="text-sm text-ink/85 leading-relaxed">{s.desc}</p>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function ClassificationsSection() {
  return (
    <div>
      <h2 className="font-serif text-3xl mb-2">Material classifications</h2>
      <p className="text-sm text-muted_text mb-2">Every fibre in every item we review is mapped to one of three colours. See the full library in the <Link to="/materials-guide" className="underline underline-offset-4 font-medium text-ink">Materials Guide</Link>.</p>
      <p className="text-sm text-muted_text mb-2">A material classification on an individual item of clothing can be overridden if the brand holds relevant <Link to="/materials-certifications" className="underline underline-offset-4 font-medium text-ink">Materials Certifications</Link> that apply to that item.</p>
      <p className="text-sm text-muted_text mb-8">The material context of a clothing item is also evaluated taking into account whether the company holds any <Link to="/brand-certifications" className="underline underline-offset-4 font-medium text-ink">Brand Certifications</Link> — company-level credentials that signal broader sustainability and ethical practices.</p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="methodology-classifications">
        <ClassCard
          color="leaf"
          label="GREEN"
          line="Sustainable, organic, or biodegradable."
          body="Materials that meet our standards on impact, sourcing, and end-of-life."
        />
        <ClassCard
          color="amber"
          label="YELLOW"
          line="Context-dependent."
          body="Partially sustainable or process-dependent. Items composed of these materials are accepted and displayed on the website depending on the context — each is reviewed individually."
        />
        <ClassCard
          color="clay"
          label="RED"
          line="Synthetic, plastic-based, or misaligned with our criteria."
          body="Items containing these materials are only displayed if the total share of synthetic content does not exceed 5% of the composition — and even then, each item is subject to further review to confirm whether it belongs on the site."
        />
      </div>
    </div>
  );
}

function ScoreSection() {
  return (
    <div>
      <h2 className="font-serif text-3xl mb-2">Sustainability score</h2>
      <p className="text-sm text-muted_text mb-6">Each brand has a percentage score calculated as:</p>

      <div className="bg-white border border-line rounded-2xl p-6 sm:p-8 mb-6">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-2 font-serif text-2xl sm:text-3xl">
          <span className="italic">Score</span>
          <span className="text-muted_text">=</span>
          <span className="text-leaf">Approved items</span>
          <span className="text-muted_text">÷</span>
          <span>Reviewed items</span>
          <span className="text-muted_text">× 100</span>
        </div>
      </div>
      <p className="text-sm text-muted_text">
        It tells you, of the products we've reviewed for that brand, how many actually met our standards.
      </p>
    </div>
  );
}

function WarningsSection() {
  const items = [
    {
      label: "Materials mislabeled",
      body: "When a reader-submitted label or our own review uncovers a clothing item whose actual composition contradicts what the brand publishes, we flag it. The warning shows on the affected item and rolls up to the brand's page.",
    },
    {
      label: "Materials disputed",
      body: "When third-party sources — independent labs, regulators, journalists, or watchdog organisations — raise credible questions about a brand's material claims, we record a disputed-materials warning until the brand provides verifiable evidence.",
    },
    {
      label: "Public-record concerns",
      body: "News reports, legal actions, or other public information showing the brand engages in practices that contradict its sustainability claims (greenwashing, undisclosed supply-chain issues, environmental violations, etc.) are captured here. Each item links to the source so readers can verify.",
    },
  ];
  return (
    <div>
      <h2 className="font-serif text-3xl mb-2">Brand warnings</h2>
      <p className="text-sm text-muted_text mb-8">Warning flags surface anything that contradicts what a brand publicly claims. There are three types:</p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="methodology-warnings">
        {items.map((it) => (
          <div key={it.label} className="bg-white border border-line rounded-2xl p-5" data-testid={`methodology-warning-${it.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-clay shrink-0" />
              <h3 className="font-serif text-lg leading-snug">{it.label}</h3>
            </div>
            <p className="text-sm text-ink/85 leading-relaxed">{it.body}</p>
          </div>
        ))}
      </div>

      <p className="text-xs text-muted_text mt-6">
        Spotted something off?{" "}
        <Link to="/contact" className="underline underline-offset-4 font-medium text-ink">Let us know</Link>.
      </p>
    </div>
  );
}

function LimitationsSection() {
  return (
    <div>
      <h2 className="font-serif text-3xl mb-2">Limitations</h2>
      <p className="text-sm text-muted_text">
        Product compositions change. Brands re-source materials. We do our best with publicly available information and reader-submitted label evidence. If you've spotted a mismatch, please{" "}
        <Link to="/contact" className="underline underline-offset-4 font-medium text-ink">tell us</Link>.
      </p>
    </div>
  );
}

function ClassCard({ color, label, line, body }) {
  const palette = {
    leaf: { bar: "bg-leaf", text: "text-leaf" },
    amber: { bar: "bg-amber", text: "text-amber" },
    clay: { bar: "bg-clay", text: "text-clay" },
  }[color];
  return (
    <div className="bg-white border border-line rounded-2xl p-5" data-testid={`methodology-class-${color}`}>
      <div className={`w-10 h-2 ${palette.bar} rounded-full mb-3`} />
      <h3 className={`font-serif text-2xl ${palette.text} mb-2`}>{label}</h3>
      <p className="font-medium text-sm text-ink mb-2">{line}</p>
      <p className="text-sm text-ink/85 leading-relaxed">{body}</p>
    </div>
  );
}
